/**
 * loginNonce — WHO OWNS THIS HISTORY ENTRY.
 *
 * Card PIPELINE-PLACE-AND-FOCUS, review round 5 (Cowork, PR #620, 11 Oct 2026).
 *
 * ── ⛔⛔ WHAT THIS CLOSES ───────────────────────────────────────────────────
 *
 * Rounds 3 and 4 made the entry the app is STANDING ON correct, and the entry
 * directly beneath it correct. Neither reached the rest of the stack.
 *
 * MEASURED in Chrome and WebKit on the previous head: user A opens contact 5
 * on the pipeline board and presses Back. The contact closes — and the entry
 * that held it survives as a FORWARD entry, still carrying `contact=5`, the
 * pipeline, the stage and A's marker. Sign-out rewrites only the entry we are
 * standing on, so that one is untouched. B then signs in **in the same
 * document**, presses Forward, and App adopts `page=crm` from that entry;
 * CRMPage remounts, reads `?contact=`, and opens A's record in B's workspace —
 * where it is persisted to B's localStorage.
 *
 * ── THE ANSWER: EVERY ENTRY THE APP WRITES IS OWNED ────────────────────────
 *
 * A marker on one entry was never going to be enough, because the leak is a
 * property of entries we are NOT standing on and cannot reach. So ownership is
 * stamped on EVERY entry this app writes — `src/lib/urlState.js` on every
 * `writeParams`, and `src/lib/browserBackStep.js` on every push — and a reader
 * that lands on an entry stamped by somebody else adopts NOTHING from it.
 *
 * ── ⚠ WHY sessionStorage, AND NOT A MODULE VARIABLE ───────────────────────
 *
 * Round 4 derived the nonce during render from the user id and a counter. That
 * is per-DOCUMENT, so a RELOAD minted a new one — and every entry written
 * before the reload, including the one being reloaded, would have read as
 * somebody else's. Reload-restore, the thing Card PARTNER-LIST-REFRESH exists
 * for, would have broken the moment ownership was enforced.
 *
 * The nonce therefore lives in sessionStorage, which is scoped to the TAB and
 * survives a reload:
 *
 *   MINTED   at sign-in (and on a cold load that finds a live session but no
 *            nonce — a fresh tab, where there are no entries of ours anyway).
 *   KEPT     across reloads, so the same person returns to the same place.
 *   DELETED  at sign-out and on idle timeout, so the next login cannot match it.
 *
 * ⛔ IT IS NOT A SECRET AND NOT A SESSION TOKEN. It is an opaque, meaningless
 * string whose only job is "was this entry written by the login that is signed
 * in right now?". It carries no user id, no name and nothing derived from
 * either — a history entry is readable by anyone at the keyboard, which is the
 * whole problem this module exists to solve, so the owner tag must disclose
 * nothing about the owner.
 */
"use strict";

const KEY = 'zg.login.v1';

/** The key history.state carries. Namespaced so nothing else can collide. */
export const OWNER_STATE_KEY = 'zgOwner';

function store() {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    // Accessing the property itself throws when site data is blocked.
    return null;
  }
}

/**
 * ⛔ OPAQUE BY CONSTRUCTION. Random, with no user id and nothing derived from
 * one. Two logins must differ; beyond that the value means nothing.
 */
function freshValue() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `${Date.now().toString(36)}.${Math.random().toString(36).slice(2, 12)}`;
}

/** @returns {string} the nonce for the signed-in login, or '' when signed out. */
export function currentLoginNonce() {
  const s = store();
  if (!s) return '';
  try { return s.getItem(KEY) || ''; } catch { return ''; }
}

/**
 * Mint a nonce for a login, unless this tab already holds one.
 *
 * ⚠ `keepExisting` is the reload path and is the default: a cold load that
 * finds a live session must NOT mint, or it would disown the very entry it is
 * about to restore from. A genuine sign-in passes false.
 *
 * @returns {string} the nonce now in force.
 */
export function mintLoginNonce({ keepExisting = true } = {}) {
  const s = store();
  if (!s) return '';
  try {
    if (keepExisting) {
      const existing = s.getItem(KEY);
      if (existing) return existing;
    }
    const v = freshValue();
    s.setItem(KEY, v);
    return v;
  } catch {
    // No storage: ownership cannot be enforced in this browser. Returning ''
    // means every entry reads as unowned, which is the PERMISSIVE direction —
    // the app keeps working and the cross-user defence degrades to the other
    // measures (the sign-out scrub and the per-entry marker). Stated rather
    // than hidden: a browser that blocks session storage does not get this one.
    return '';
  }
}

/** Sign-out and idle timeout. After this, nothing in history reads as ours. */
export function clearLoginNonce() {
  const s = store();
  if (!s) return;
  try { s.removeItem(KEY); } catch { /* nothing to do */ }
}

/**
 * Does this history entry belong to the login that is signed in right now?
 *
 * ⚠⚠ AN ENTRY WITH NO OWNER IS NOT A STRANGER'S — it is NOBODY'S, and that is
 * the ordinary case for an arrival from outside: a pasted deep link, a
 * bookmark, a typed URL. Those must keep working (tests/react and the S9
 * browser scenario both drive one). What must be refused is an entry stamped
 * by a DIFFERENT login, which can only have been written by this app, in this
 * tab, by whoever was signed in before.
 *
 * @param {*} state  a history.state object (or null)
 * @returns {boolean}
 */
export function entryIsOurs(state) {
  const stamped = state && typeof state === 'object' ? state[OWNER_STATE_KEY] : undefined;
  if (!stamped) return true;                       // unowned — an outside arrival
  return stamped === currentLoginNonce();
}

/** Stamp an owner onto a history state object. Removes it when signed out. */
export function stampOwner(state) {
  const next = { ...(state && typeof state === 'object' ? state : {}) };
  const nonce = currentLoginNonce();
  if (nonce) next[OWNER_STATE_KEY] = nonce;
  else delete next[OWNER_STATE_KEY];
  return next;
}
